import { useState, useEffect } from 'react';
import Breadcrumb from '~/components/Breadcrumbs/Breadcrumb';
import Panel from '~/components/Panel';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
    approveRejectVendorChangeRequest,
    fetchVendorChangeRequest,
} from '~/services/fund-requests';
import { VendorChangeRequest } from '~/types/fundrequests';
import toast from 'react-hot-toast';
import { useQuery } from '@tanstack/react-query';
import { RxCross2, RxCheck } from 'react-icons/rx';
import { MdContentCopy } from 'react-icons/md';
import TitlePanel from '~/components/FundRequests/RequestChangeDetails/TitlePanel';
import { FundrequestLightbox } from '~/components/FundRequests/drawers/ExpenseInfoDrawer/Lightbox';
import { Country } from 'country-state-city';

// Interface to track field changes
interface FieldChange {
    oldValue: any;
    newValue: any;
    hasChanged: boolean;
}

interface FieldComparisonResult {
    changes: Record<string, FieldChange>;
    changeCount: number;
}

// Extend the VendorChangeRequest interface to include address_label property
interface ExtendedVendorChangeRequest extends VendorChangeRequest {
    address_label?: string;
    last_ach_change_approval_date?: string;
}

// Function to compare old and new values and determine if there are changes
const compareVendorFields = (
    vendorChangeRequest: ExtendedVendorChangeRequest
): FieldComparisonResult => {
    const fieldMappings: Record<
        string,
        { oldField: string; newField: string }
    > = {
        vendor_id: {
            oldField: 'vendor_address.vendor_id',
            newField: 'vendor_id',
        },
        name: { oldField: 'vendor.name', newField: 'name' },
        address_label: {
            oldField: 'vendor_address.address_label',
            newField: 'address_label',
        },
        address_1: {
            oldField: 'vendor_address.address_1',
            newField: 'address_1',
        },
        address_2: {
            oldField: 'vendor_address.address_2',
            newField: 'address_2',
        },
        city: { oldField: 'vendor_address.city', newField: 'city' },
        country: {
            oldField: 'vendor_address.country',
            newField: 'country',
        },
        state: {
            oldField: 'vendor_address.state.abbreviation',
            newField: 'state.abbreviation',
        },
        state_province: {
            oldField: 'vendor_address.state_province',
            newField: 'state_province',
        },
        zip_code: { oldField: 'vendor_address.zip_code', newField: 'zip_code' },
        phone: { oldField: 'vendor.phone', newField: 'phone' },
        email: { oldField: 'vendor.email', newField: 'email' },
        payment_type: {
            oldField: 'vendor_address.payment_type',
            newField: 'preferred_payment_method',
        },
        account_type: {
            oldField: 'vendor_address.ach_type',
            newField: 'account_type',
        },
    };

    const result: Record<string, FieldChange> = {};
    let count = 0;

    // Helper function to get nested property values
    const getNestedValue = (obj: any, path: string) => {
        return path.split('.').reduce((prev, curr) => {
            return prev && prev[curr] !== undefined ? prev[curr] : null;
        }, obj);
    };

    // Compare all mapped fields
    Object.entries(fieldMappings).forEach(([key, { oldField, newField }]) => {
        const oldValue = getNestedValue(vendorChangeRequest, oldField);
        const newValue = getNestedValue(vendorChangeRequest, newField);

        // Special handling for address_2 which might be empty string or null
        if (key === 'address_2') {
            // Normalize values: treat empty strings, null, and undefined as equivalent
            const normalizedOldValue = oldValue ? oldValue.trim() : '';
            const normalizedNewValue = newValue ? newValue.trim() : '';

            const hasChanged =
                normalizedOldValue !== normalizedNewValue &&
                (normalizedNewValue !== '' || normalizedOldValue !== '');

            result[key] = {
                oldValue,
                newValue,
                hasChanged,
            };

            if (hasChanged) {
                count++;
            }
        } else {
            // Check if there is a change (new value exists and is different from old value)
            const hasChanged =
                newValue !== null &&
                newValue !== oldValue &&
                String(newValue || '').trim() !== String(oldValue || '').trim();

            result[key] = {
                oldValue,
                newValue,
                hasChanged,
            };

            if (hasChanged) {
                count++;
            }
        }
    });

    // Add special cases for address operations
    if (vendorChangeRequest.delete_address) {
        result.delete_address = {
            oldValue: false,
            newValue: true,
            hasChanged: true,
        };
        count++;
    }

    if (vendorChangeRequest.make_primary) {
        result.make_primary = {
            oldValue: false,
            newValue: true,
            hasChanged: true,
        };
        count++;
    }

    // Handle document type and URL as one change
    if (vendorChangeRequest.ach_document_url) {
        result.ach_document = {
            oldValue: null,
            newValue: vendorChangeRequest.ach_document_url,
            hasChanged: true,
        };
        count++;
    }

    return {
        changes: result,
        changeCount: count,
    };
};

const RequestChangeDetail: React.FC = () => {
    const [vendorChangeRequest, setVendorChangeRequest] =
        useState<ExtendedVendorChangeRequest | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [processing, setProcessing] = useState<boolean>(false);
    const [fieldChanges, setFieldChanges] = useState<
        Record<string, FieldChange>
    >({});
    const [changeCount, setChangeCount] = useState<number>(0);
    const [openLightbox, setOpenLightbox] = useState<boolean>(false);
    const [mipConfirmed, setMipConfirmed] = useState<boolean>(false);

    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const getVendorChangeRequest = async () => {
        if (!id) return null;
        setLoading(true);
        try {
            const resp = await fetchVendorChangeRequest(id);
            setVendorChangeRequest(resp as ExtendedVendorChangeRequest);

            // Calculate field changes and change count
            const result = compareVendorFields(
                resp as ExtendedVendorChangeRequest
            );
            setFieldChanges(result.changes);
            setChangeCount(result.changeCount);

            return resp;
        } catch (error) {
            toast.error('Failed to fetch vendor change request');
            console.error(error);
            return null;
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async () => {
        if (!id) return;

        if (!mipConfirmed) {
            toast.error('Please confirm that you have entered the information in MIP');
            return;
        }

        setProcessing(true);
        try {
            await toast.promise(
                approveRejectVendorChangeRequest(id, 'approve'),
                {
                    loading: 'Verifying vendor changes...',
                    success: 'Vendor change request approved successfully',
                    error: (error) => `Failed to approve vendor change request: ${error.message}`
                }
            );
            navigate('/payees/request-changes');
        } catch (error) {
            console.error(error);
        } finally {
            setProcessing(false);
        }
    };

    const handleReject = async (reason: string) => {
        if (!id || !reason.trim()) {
            toast.error('Please provide a reason for rejection');
            return;
        }

        setProcessing(true);
        try {
            await approveRejectVendorChangeRequest(id, 'reject', reason);
            toast.success('Vendor change request rejected');
            navigate('/payees/request-changes');
        } catch (error) {
            toast.error('Failed to reject vendor change request');
            console.error(error);
        } finally {
            setProcessing(false);
        }
    };

    useQuery({
        queryKey: ['vendorChangeRequest', id],
        queryFn: getVendorChangeRequest,
        retry: false,
    });

    useEffect(() => {
        if (vendorChangeRequest) {
            const result = compareVendorFields(vendorChangeRequest);
            setFieldChanges(result.changes);
            setChangeCount(result.changeCount);

            // Ensure vendor_id field exists after setting field changes
            if (
                !('vendor_id' in result.changes) &&
                vendorChangeRequest.vendor_address?.vendor_id
            ) {
                const vendorId = vendorChangeRequest.vendor_address.vendor_id;
                setFieldChanges(prev => ({
                    ...prev,
                    vendor_id: {
                        oldValue: vendorId,
                        newValue: vendorId,
                        hasChanged: false,
                    },
                }));
            }
        }
    }, [vendorChangeRequest]);

    // Function to check if old banking information is present
    const hasOldBankingInfo = () => {
        if (!vendorChangeRequest) return false;

        return !!(
            vendorChangeRequest.vendor_address?.payment_type ||
            vendorChangeRequest.vendor_address?.ach_type ||
            vendorChangeRequest.vendor_address?.ach_last_4
        );
    };

    // Function to check if new banking information is present
    const hasNewBankingInfo = () => {
        if (!vendorChangeRequest) return false;

        return !!(
            vendorChangeRequest.preferred_payment_method ||
            vendorChangeRequest.account_type ||
            vendorChangeRequest.routing_number ||
            vendorChangeRequest.account_number ||
            vendorChangeRequest.ach_document_url
        );
    };

    const formatAccountType = (value: string | null) => {
        if (!value) return '';
        if (value === 'CHECKING') return 'Checking';
        if (value === 'SAVINGS') return 'Savings';
        return value;
    };

    const getCountryName = (code: string | undefined | null) => {
        if (!code) return '';
        return Country.getCountryByCode(code)?.name || code;
    };

    const handleCopy = (text: string, label: string) => {
        navigator.clipboard.writeText(text);
        toast.success(`${label} copied to clipboard`);
    };

    if (loading || !vendorChangeRequest) {
        return <div>Loading...</div>;
    }

    // Determine if the request is already approved or rejected
    const isApproved = !!vendorChangeRequest.approved_date;
    const isRejected = !!vendorChangeRequest.rejected_date;
    const isPending = !isApproved && !isRejected;

    // Function to render field in the left panel (old value)
    const renderOldField = (fieldKey: string, label: string) => {
        if (!vendorChangeRequest || !fieldChanges[fieldKey]) return null;

        const { oldValue, hasChanged } = fieldChanges[fieldKey];

        if (!oldValue) return null;

        return (
            <div className='form-group mb-3'>
                <label className='mb-2 block font-bold'>{label}</label>
                <div
                    className={
                        hasChanged
                            ? 'flex justify-between bg-[#FDEAEB] p-2 font-bold'
                            : ''
                    }
                >
                    {oldValue}
                    {hasChanged && (
                        <RxCross2
                            color='#F02D3A'
                            size={22}
                            className='cursor-pointer'
                        />
                    )}
                </div>
            </div>
        );
    };

    // Function to render field in the right panel (new value)
    const renderNewField = (fieldKey: string, field: FieldChange) => {
        if (!vendorChangeRequest || !field) return null;

        const { newValue, oldValue, hasChanged } = field;

        // If the field exists in the left panel (oldValue exists), we should display it in the right panel too
        if (!oldValue && !newValue) return null;

        return (
            <div className='form-group mb-3'>
                <label className='mb-2 block font-bold'>{fieldKey}</label>
                {hasChanged ? (
                    <div className='flex justify-between bg-[#F7F8EB] p-2 font-bold'>
                        {newValue}
                        <RxCheck
                            color='#AFC12D'
                            size={26}
                            className='cursor-pointer'
                        />
                    </div>
                ) : (
                    // If there's no change, show the old value without indicators
                    <div>{oldValue}</div>
                )}
            </div>
        );
    };

    return (
        <>
            <Breadcrumb
                pageName='Vendor Change Request'
                parentPage='Change Requests'
                parentPath={`/payees/request-changes${location.search}`}
            />

            <TitlePanel
                vendorChangeRequest={vendorChangeRequest}
                isProcessing={processing}
                onCancel={() => navigate('/payees/request-changes')}
                onReject={handleReject}
                onApprove={handleApprove}
                isRejected={isRejected}
                isApproved={isApproved}
                changeCount={changeCount}
                mipConfirmed={mipConfirmed}
                setMipConfirmed={setMipConfirmed}
            />

            <div className='grid grid-cols-12'>
                <section className='col-span-12'>
                    <div className='mb-7.5'>
                        {isRejected ? (
                            <Panel addClasses='bg-[#FDEAEB] border-[#FDEAEB]'>
                                <div className='mb-4'>
                                    <h2 className='text-[22px]/[28px] font-bold'>
                                        Reason for Rejection
                                    </h2>
                                </div>
                                <div className='xl:w-1/2'>
                                    {vendorChangeRequest.reason_for_rejection ||
                                        'No reason provided'}
                                </div>
                            </Panel>
                        ) : null}

                        {/* Banner for delete address request */}
                        {vendorChangeRequest.delete_address ? (
                            <Panel addClasses='bg-[#FDEAEB] border-[#FDEAEB]'>
                                <div className='mb-4'>
                                    <h2 className='text-[22px]/[28px] font-bold'>
                                        Request to delete secondary address
                                    </h2>
                                </div>
                                <div className='xl:w-1/2'>
                                    They are requesting to delete the address
                                    for{' '}
                                    <span className='font-bold'>
                                        {vendorChangeRequest.vendor_address
                                            ?.address_label ||
                                            'Unnamed Address'}
                                    </span>
                                    .
                                </div>
                            </Panel>
                        ) : null}

                        {/* Banner for make address primary request */}
                        {vendorChangeRequest.make_primary ? (
                            <Panel addClasses='bg-[#F7F8EB] border-[#F7F8EB]'>
                                <div className='mb-4'>
                                    <h2 className='text-[22px]/[28px] font-bold'>
                                        Request to make secondary address
                                        primary
                                    </h2>
                                </div>
                                <div className='xl:w-1/2'>
                                    They are requesting to make the address for{' '}
                                    <span className='font-bold'>
                                        {vendorChangeRequest.vendor_address
                                            ?.address_label ||
                                            'Unnamed Address'}
                                    </span>{' '}
                                    their primary address.
                                </div>
                            </Panel>
                        ) : null}
                    </div>
                </section>
            </div>

            <div className='grid grid-cols-12 gap-4'>
                <section className='col-span-12 xl:col-span-6'>
                    <div className='mb-7.5'>
                        <Panel>
                            {/* Header */}
                            <div className='mb-4'>
                                <h2 className='text-[22px]/[28px] font-bold'>
                                    Old Vendor Info
                                </h2>
                            </div>

                                    <h3 className='mb-3 text-[18px]/[22px] font-medium uppercase tracking-[1.44px] text-[#131212]'>
                                        Contact Information
                                    </h3>

                                    {/* Vendor ID */}
                                    {renderOldField('vendor_id', 'Vendor ID')}

                                    {/* Vendor Name */}
                                    <div className='form-group mb-3'>
                                        <label className='mb-2 block font-bold'>
                                            Vendor Name
                                        </label>
                                        <div
                                            className={
                                                fieldChanges['name']?.hasChanged
                                                    ? 'flex justify-between bg-[#FDEAEB] p-2 font-bold'
                                                    : ''
                                            }
                                        >
                                            {vendorChangeRequest.vendor?.name}
                                            {fieldChanges['name']?.hasChanged && (
                                                <RxCross2
                                                    color='#F02D3A'
                                                    size={22}
                                                    className='cursor-pointer'
                                                />
                                            )}
                                        </div>
                                    </div>

                                    {/* Phone */}
                                    <div
                                        className='form-group mb-3'
                                        style={{ maxWidth: 300 }}
                                    >
                                        <label className='mb-2 block font-bold'>
                                            Phone
                                        </label>
                                        <div
                                            className={
                                                fieldChanges['phone']?.hasChanged
                                                    ? 'flex justify-between bg-[#FDEAEB] p-2 font-bold'
                                                    : ''
                                            }
                                        >
                                            {vendorChangeRequest.vendor?.phone}
                                            {fieldChanges['phone']?.hasChanged && (
                                                <RxCross2
                                                    color='#F02D3A'
                                                    size={22}
                                                    className='cursor-pointer'
                                                />
                                            )}
                                        </div>
                                    </div>

                                    {/* Email */}
                                    <div
                                        className='form-group mb-5'
                                        style={{ maxWidth: 300 }}
                                    >
                                        <label className='mb-2 block font-bold'>
                                            Email
                                        </label>
                                        <div
                                            className={
                                                fieldChanges['email']?.hasChanged
                                                    ? 'flex justify-between bg-[#FDEAEB] p-2 font-bold'
                                                    : ''
                                            }
                                        >
                                            {vendorChangeRequest.vendor?.email}
                                            {fieldChanges['email']?.hasChanged && (
                                                <RxCross2
                                                    color='#F02D3A'
                                                    size={22}
                                                    className='cursor-pointer'
                                                />
                                            )}
                                        </div>
                                    </div>

                            {vendorChangeRequest.vendor_address && (
                                <>
                                    <h3 className='mb-3 mt-2 text-[18px]/[22px] font-medium uppercase tracking-[1.44px] text-[#131212]'>
                                        Check Address Information
                                    </h3>

                                    {/* Address Label */}
                                    <div className='form-group mb-3'>
                                        <label className='mb-2 block font-bold'>
                                            Address Label
                                        </label>
                                        <div
                                            className={
                                                fieldChanges['address_label']
                                                    ?.hasChanged
                                                    ? 'flex justify-between bg-[#FDEAEB] p-2 font-bold'
                                                    : ''
                                            }
                                        >
                                            {vendorChangeRequest.vendor_address
                                                ?.address_label || (
                                                    <span className='text-gray-400'>
                                                        No label
                                                    </span>
                                                )}
                                            {fieldChanges['address_label']
                                                ?.hasChanged && (
                                                    <RxCross2
                                                        color='#F02D3A'
                                                        size={22}
                                                        className='cursor-pointer'
                                                    />
                                                )}
                                        </div>
                                    </div>

                                    {/* Is Primary */}
                                    <div className='form-group mb-3'>
                                        <label className='mb-2 block font-bold'>
                                            Is Primary
                                        </label>
                                        <div>
                                            {vendorChangeRequest.vendor_address
                                                ?.is_primary
                                                ? 'Yes'
                                                : 'No'}
                                        </div>
                                    </div>

                                    {/* Street Address 1 */}
                                    <div className='form-group mb-3'>
                                        <label className='mb-2 block font-bold'>
                                            Street Address 1
                                        </label>
                                        <div
                                            className={
                                                fieldChanges['address_1']?.hasChanged
                                                    ? 'flex justify-between bg-[#FDEAEB] p-2 font-bold'
                                                    : ''
                                            }
                                        >
                                            {
                                                vendorChangeRequest.vendor_address
                                                    ?.address_1
                                            }
                                            {fieldChanges['address_1']?.hasChanged && (
                                                <RxCross2
                                                    color='#F02D3A'
                                                    size={22}
                                                    className='cursor-pointer'
                                                />
                                            )}
                                        </div>
                                    </div>

                                    {/* Street Address 2 */}
                                    <div className='form-group mb-3'>
                                        <label className='mb-2 block font-bold'>
                                            Street Address 2
                                        </label>
                                        <div
                                            className={
                                                fieldChanges['address_2']?.hasChanged
                                                    ? 'flex justify-between bg-[#FDEAEB] p-2 font-bold'
                                                    : ''
                                            }
                                        >
                                            {vendorChangeRequest.vendor_address
                                                ?.address_2 || (
                                                    <span className='text-gray-400'>
                                                        Not provided
                                                    </span>
                                                )}
                                            {fieldChanges['address_2']?.hasChanged && (
                                                <RxCross2
                                                    color='#F02D3A'
                                                    size={22}
                                                    className='cursor-pointer'
                                                />
                                            )}
                                        </div>
                                    </div>

                                    {/* Country - only show for non-US */}
                                    {vendorChangeRequest.vendor_address?.country && vendorChangeRequest.vendor_address.country !== 'US' && (
                                        <div className='form-group mb-3'>
                                            <label className='mb-2 block font-bold'>
                                                Country
                                            </label>
                                            <div
                                                className={
                                                    fieldChanges['country']?.hasChanged
                                                        ? 'flex justify-between bg-[#FDEAEB] p-2 font-bold'
                                                        : ''
                                                }
                                            >
                                                {getCountryName(vendorChangeRequest.vendor_address?.country)}
                                                {fieldChanges['country']?.hasChanged && (
                                                    <RxCross2
                                                        color='#F02D3A'
                                                        size={22}
                                                        className='cursor-pointer'
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* City, State/Province, Zip/Postal Code */}
                                    <div className='form-group mb-3 flex gap-8'>
                                        {/* City */}
                                        <div className='form-group flex-1'>
                                            <label className='mb-2 block font-bold'>
                                                City
                                            </label>
                                            <div
                                                className={
                                                    fieldChanges['city']?.hasChanged
                                                        ? 'flex justify-between bg-[#FDEAEB] p-2 font-bold'
                                                        : ''
                                                }
                                            >
                                                {
                                                    vendorChangeRequest.vendor_address
                                                        ?.city
                                                }
                                                {fieldChanges['city']?.hasChanged && (
                                                    <RxCross2
                                                        color='#F02D3A'
                                                        size={22}
                                                        className='cursor-pointer'
                                                    />
                                                )}
                                            </div>
                                        </div>
                                        <div className='flex-0'>
                                            <label className='mb-2 block font-bold'>
                                                {vendorChangeRequest.vendor_address?.country && vendorChangeRequest.vendor_address.country !== 'US' ? 'State/Province' : 'State'}
                                            </label>
                                            <div
                                                className={
                                                    (fieldChanges['state']?.hasChanged || fieldChanges['state_province']?.hasChanged)
                                                        ? 'flex justify-between bg-[#FDEAEB] p-2 font-bold'
                                                        : ''
                                                }
                                            >
                                                {vendorChangeRequest.vendor_address?.country && vendorChangeRequest.vendor_address.country !== 'US'
                                                    ? vendorChangeRequest.vendor_address?.state_province
                                                    : vendorChangeRequest.vendor_address?.state?.state
                                                }
                                                {(fieldChanges['state']?.hasChanged || fieldChanges['state_province']?.hasChanged) && (
                                                    <RxCross2
                                                        color='#F02D3A'
                                                        size={22}
                                                        className='cursor-pointer'
                                                    />
                                                )}
                                            </div>
                                        </div>

                                        <div className='flex-0'>
                                            <label className='mb-2 block font-bold'>
                                                {vendorChangeRequest.vendor_address?.country && vendorChangeRequest.vendor_address.country !== 'US' ? 'Postal Code' : 'Zip Code'}
                                            </label>
                                            <div
                                                className={
                                                    fieldChanges['zip_code']?.hasChanged
                                                        ? 'flex justify-between bg-[#FDEAEB] p-2 font-bold'
                                                        : ''
                                                }
                                            >
                                                {
                                                    vendorChangeRequest.vendor_address
                                                        ?.zip_code
                                                }
                                                {fieldChanges['zip_code']
                                                    ?.hasChanged && (
                                                        <RxCross2
                                                            color='#F02D3A'
                                                            size={22}
                                                            className='cursor-pointer'
                                                        />
                                                    )}
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}

                            {/* Direct Deposit Information - show only if it's an ACH change request or if relevant fields exist */}
                            {(vendorChangeRequest.ach_document_filename || hasOldBankingInfo()) && (
                                <>
                                    <h3 className='mb-3 text-[18px]/[22px] font-medium uppercase tracking-[1.44px] text-[#131212]'>
                                        Direct Deposit Information
                                    </h3>

                                    {/* Last ACH Change Approval Date */}
                                    {vendorChangeRequest.ach_document_filename && (
                                        <div className='form-group mb-3'>
                                            <label className='mb-2 block font-bold'>
                                                ACH Document
                                            </label>
                                            <div className='flex justify-between bg-[#FDEAEB] p-2 italic'>
                                                We do not store images that contain banking information after the data has been entered in MIP.
                                                <br /><br />
                                                ACH Last Updated: {vendorChangeRequest.last_ach_change_approval_date ? new Date(vendorChangeRequest.last_ach_change_approval_date).toLocaleDateString() : 'N/A'}
                                                <RxCross2
                                                    color='#F02D3A'
                                                    size={22}
                                                    className='cursor-pointer'
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {/* Payment Method */}
                                    {vendorChangeRequest.vendor_address
                                        ?.payment_type && (
                                            <div className='form-group mb-3'>
                                                <label className='mb-2 block font-bold'>
                                                    Payment Method
                                                </label>
                                                {fieldChanges['payment_type']
                                                    ?.hasChanged ? (
                                                    <div className='flex justify-between bg-[#FDEAEB] p-2 font-bold'>
                                                        {
                                                            vendorChangeRequest
                                                                .vendor_address
                                                                ?.payment_type
                                                        }
                                                        <RxCross2
                                                            color='#F02D3A'
                                                            size={22}
                                                            className='cursor-pointer'
                                                        />
                                                    </div>
                                                ) : (
                                                    <div>
                                                        {
                                                            vendorChangeRequest
                                                                .vendor_address
                                                                ?.payment_type
                                                        }
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                    {/* ACH Type */}
                                    {vendorChangeRequest.vendor_address
                                        ?.ach_type && (
                                            <div className='form-group mb-3'>
                                                <label className='mb-2 block font-bold'>
                                                    Account Type
                                                </label>
                                                {fieldChanges['account_type']
                                                    ?.hasChanged ? (
                                                    <div className='flex justify-between bg-[#FDEAEB] p-2 font-bold'>
                                                        {
                                                            vendorChangeRequest
                                                                .vendor_address
                                                                ?.ach_type
                                                        }
                                                        <RxCross2
                                                            color='#F02D3A'
                                                            size={22}
                                                            className='cursor-pointer'
                                                        />
                                                    </div>
                                                ) : (
                                                    <div>
                                                        {
                                                            vendorChangeRequest
                                                                .vendor_address
                                                                ?.ach_type
                                                        }
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                    {/* ACH Last 4 */}
                                    {vendorChangeRequest.vendor_address
                                        ?.ach_last_4 && (
                                            <div className='form-group mb-3'>
                                                <label className='mb-2 block font-bold'>
                                                    Account Last 4
                                                </label>
                                                <div>
                                                    ****
                                                    {
                                                        vendorChangeRequest
                                                            .vendor_address
                                                            ?.ach_last_4
                                                    }
                                                </div>
                                            </div>
                                        )}
                                </>
                            )}
                        </Panel>
                    </div>
                </section>

                <section className='col-span-12 lg:col-span-6'>
                    <div className='mb-7.5'>
                        <Panel>
                            {/* Header */}
                            <div className='mb-4'>
                                <h2 className='text-[22px]/[28px] font-bold'>
                                    New Vendor Info
                                </h2>
                            </div>

                                    <h3 className='mb-3 text-[18px]/[22px] font-medium uppercase tracking-[1.44px] text-[#131212]'>
                                        Contact Information
                                    </h3>

                                    {/* Vendor ID */}
                                    {renderNewField(
                                        'vendor_id',
                                        fieldChanges['vendor_id']
                                    )}

                                    {/* Vendor Name */}
                                    <div className='form-group mb-3'>
                                        <label className='mb-2 block font-bold'>
                                            Vendor Name
                                        </label>
                                        {fieldChanges['name']?.hasChanged ? (
                                            <div className='flex justify-between bg-[#F7F8EB] p-2 font-bold'>
                                                {vendorChangeRequest.vendor_address
                                                    ?.vendor_name ||
                                                    vendorChangeRequest.vendor?.name}
                                                <RxCheck
                                                    color='#AFC12D'
                                                    size={26}
                                                    className='cursor-pointer'
                                                />
                                            </div>
                                        ) : (
                                            <div>
                                                {vendorChangeRequest.vendor_address
                                                    ?.vendor_name ||
                                                    vendorChangeRequest.vendor?.name}
                                            </div>
                                        )}
                                    </div>

                                    {/* Phone */}
                                    <div
                                        className='form-group mb-3'
                                        style={{ maxWidth: 300 }}
                                    >
                                        <label className='mb-2 block font-bold'>
                                            Phone
                                        </label>
                                        {fieldChanges['phone']?.hasChanged ? (
                                            <div className='flex justify-between bg-[#F7F8EB] p-2 font-bold'>
                                                {vendorChangeRequest.phone}
                                                <RxCheck
                                                    color='#AFC12D'
                                                    size={26}
                                                    className='cursor-pointer'
                                                />
                                            </div>
                                        ) : (
                                            <div>{vendorChangeRequest.phone}</div>
                                        )}
                                    </div>

                                    {/* Email */}
                                    <div
                                        className='form-group mb-5'
                                        style={{ maxWidth: 300 }}
                                    >
                                        <label className='mb-2 block font-bold'>
                                            Email
                                        </label>
                                        {fieldChanges['email']?.hasChanged ? (
                                            <div className='flex justify-between bg-[#F7F8EB] p-2 font-bold'>
                                                {vendorChangeRequest.email}
                                                <RxCheck
                                                    color='#AFC12D'
                                                    size={26}
                                                    className='cursor-pointer'
                                                />
                                            </div>
                                        ) : (
                                            <div>{vendorChangeRequest.email}</div>
                                        )}
                                    </div>

                            {vendorChangeRequest.vendor_address && (
                                <>
                                    <h3 className='mb-3 mt-2 text-[18px]/[22px] font-medium uppercase tracking-[1.44px] text-[#131212]'>
                                        Check Address Information
                                    </h3>

                                    {/* Address Label */}
                                    <div className='form-group mb-3'>
                                        <label className='mb-2 block font-bold'>
                                            Address Label
                                        </label>
                                        {fieldChanges['address_label']?.hasChanged ? (
                                            <div className='flex justify-between bg-[#F7F8EB] p-2 font-bold'>
                                                {vendorChangeRequest.address_label || (
                                                    <span className='text-gray-400'>
                                                        No label
                                                    </span>
                                                )}
                                                <RxCheck
                                                    color='#AFC12D'
                                                    size={26}
                                                    className='cursor-pointer'
                                                />
                                            </div>
                                        ) : vendorChangeRequest.delete_address ? (
                                            <div className='flex justify-between bg-[#FDEAEB] p-2 font-bold'>
                                                <span className='line-through'>
                                                    {vendorChangeRequest.address_label || (
                                                        <span className='text-gray-400'>
                                                            No label
                                                        </span>
                                                    )}
                                                </span>
                                                <RxCross2
                                                    color='#F02D3A'
                                                    size={22}
                                                    className='cursor-pointer'
                                                />
                                            </div>
                                        ) : (
                                            <div>
                                                {vendorChangeRequest.address_label || (
                                                    <span className='text-gray-400'>
                                                        No label
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {/* Is Primary */}
                                    <div className='form-group mb-3'>
                                        <label className='mb-2 block font-bold'>
                                            Is Primary
                                        </label>
                                        {vendorChangeRequest.make_primary ? (
                                            <div className='flex justify-between bg-[#F7F8EB] p-2 font-bold'>
                                                Yes
                                                <RxCheck
                                                    color='#AFC12D'
                                                    size={26}
                                                    className='cursor-pointer'
                                                />
                                            </div>
                                        ) : vendorChangeRequest.vendor_address
                                            ?.is_primary ? (
                                            <div>Yes</div>
                                        ) : (
                                            <div>No</div>
                                        )}
                                    </div>

                                    {/* Street Address 1 */}
                                    <div className='form-group mb-3'>
                                        <label className='mb-2 block font-bold'>
                                            Street Address 1
                                        </label>
                                        {fieldChanges['address_1']?.hasChanged ? (
                                            <div className='flex justify-between bg-[#F7F8EB] p-2 font-bold'>
                                                {vendorChangeRequest.address_1}
                                                <RxCheck
                                                    color='#AFC12D'
                                                    size={26}
                                                    className='cursor-pointer'
                                                />
                                            </div>
                                        ) : vendorChangeRequest.delete_address ? (
                                            <div className='flex justify-between bg-[#FDEAEB] p-2 font-bold'>
                                                <span className='line-through'>
                                                    {vendorChangeRequest.address_1}
                                                </span>
                                                <RxCross2
                                                    color='#F02D3A'
                                                    size={22}
                                                    className='cursor-pointer'
                                                />
                                            </div>
                                        ) : (
                                            <div>{vendorChangeRequest.address_1}</div>
                                        )}
                                    </div>

                                    {/* Street Address 2 */}
                                    <div className='form-group mb-3'>
                                        <label className='mb-2 block font-bold'>
                                            Street Address 2
                                        </label>
                                        {fieldChanges['address_2']?.hasChanged ? (
                                            <div className='flex justify-between bg-[#F7F8EB] p-2 font-bold'>
                                                {vendorChangeRequest.address_2 || (
                                                    <span className='text-gray-400'>
                                                        Not provided
                                                    </span>
                                                )}
                                                <RxCheck
                                                    color='#AFC12D'
                                                    size={26}
                                                    className='cursor-pointer'
                                                />
                                            </div>
                                        ) : vendorChangeRequest.delete_address ? (
                                            <div className='flex justify-between bg-[#FDEAEB] p-2 font-bold'>
                                                <span className='line-through'>
                                                    {vendorChangeRequest.address_2 || (
                                                        <span className='text-gray-400'>
                                                            Not provided
                                                        </span>
                                                    )}
                                                </span>
                                                <RxCross2
                                                    color='#F02D3A'
                                                    size={22}
                                                    className='cursor-pointer'
                                                />
                                            </div>
                                        ) : (
                                            <div>
                                                {vendorChangeRequest.address_2 || (
                                                    <span className='text-gray-400'>
                                                        Not provided
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {/* Country - only show for non-US */}
                                    {vendorChangeRequest.country && vendorChangeRequest.country !== 'US' && (
                                        <div className='form-group mb-3'>
                                            <label className='mb-2 block font-bold'>
                                                Country
                                            </label>
                                            {fieldChanges['country']?.hasChanged ? (
                                                <div className='flex justify-between bg-[#F7F8EB] p-2 font-bold'>
                                                    {getCountryName(vendorChangeRequest.country)}
                                                    <RxCheck
                                                        color='#AFC12D'
                                                        size={26}
                                                        className='cursor-pointer'
                                                    />
                                                </div>
                                            ) : vendorChangeRequest.delete_address ? (
                                                <div className='flex justify-between bg-[#FDEAEB] p-2 font-bold'>
                                                    <span className='line-through'>
                                                        {getCountryName(vendorChangeRequest.country)}
                                                    </span>
                                                    <RxCross2
                                                        color='#F02D3A'
                                                        size={22}
                                                        className='cursor-pointer'
                                                    />
                                                </div>
                                            ) : (
                                                <div>{getCountryName(vendorChangeRequest.country)}</div>
                                            )}
                                        </div>
                                    )}

                                    {/* City, State/Province, Zip/Postal Code */}
                                    <div className='form-group mb-3 flex gap-8'>
                                        {/* City */}
                                        <div className='form-group flex-1'>
                                            <label className='mb-2 block font-bold'>
                                                City
                                            </label>
                                            {fieldChanges['city']?.hasChanged ? (
                                                <div className='flex justify-between bg-[#F7F8EB] p-2 font-bold'>
                                                    {vendorChangeRequest.city}
                                                    <RxCheck
                                                        color='#AFC12D'
                                                        size={26}
                                                        className='cursor-pointer'
                                                    />
                                                </div>
                                            ) : vendorChangeRequest.delete_address ? (
                                                <div className='flex justify-between bg-[#FDEAEB] p-2 font-bold'>
                                                    <span className='line-through'>
                                                        {vendorChangeRequest.city}
                                                    </span>
                                                    <RxCross2
                                                        color='#F02D3A'
                                                        size={22}
                                                        className='cursor-pointer'
                                                    />
                                                </div>
                                            ) : (
                                                <div>{vendorChangeRequest.city}</div>
                                            )}
                                        </div>
                                        <div className='flex-0'>
                                            <label className='mb-2 block font-bold'>
                                                {vendorChangeRequest.country && vendorChangeRequest.country !== 'US' ? 'State/Province' : 'State'}
                                            </label>
                                            {(fieldChanges['state']?.hasChanged || fieldChanges['state_province']?.hasChanged) ? (
                                                <div className='flex justify-between bg-[#F7F8EB] p-2 font-bold'>
                                                    {vendorChangeRequest.country && vendorChangeRequest.country !== 'US'
                                                        ? vendorChangeRequest.state_province
                                                        : vendorChangeRequest.state?.state
                                                    }
                                                    <RxCheck
                                                        color='#AFC12D'
                                                        size={26}
                                                        className='cursor-pointer'
                                                    />
                                                </div>
                                            ) : vendorChangeRequest.delete_address ? (
                                                <div className='flex justify-between bg-[#FDEAEB] p-2 font-bold'>
                                                    <span className='line-through'>
                                                        {vendorChangeRequest.country && vendorChangeRequest.country !== 'US'
                                                            ? vendorChangeRequest.state_province
                                                            : vendorChangeRequest.state?.state
                                                        }
                                                    </span>
                                                    <RxCross2
                                                        color='#F02D3A'
                                                        size={22}
                                                        className='cursor-pointer'
                                                    />
                                                </div>
                                            ) : (
                                                <div>
                                                    {vendorChangeRequest.country && vendorChangeRequest.country !== 'US'
                                                        ? vendorChangeRequest.state_province
                                                        : vendorChangeRequest.state?.state
                                                    }
                                                </div>
                                            )}
                                        </div>

                                        <div className='flex-0'>
                                            <label className='mb-2 block font-bold'>
                                                {vendorChangeRequest.country && vendorChangeRequest.country !== 'US' ? 'Postal Code' : 'Zip Code'}
                                            </label>
                                            {fieldChanges['zip_code']?.hasChanged ? (
                                                <div className='flex justify-between bg-[#F7F8EB] p-2 font-bold'>
                                                    {vendorChangeRequest.zip_code}
                                                    <RxCheck
                                                        color='#AFC12D'
                                                        size={26}
                                                        className='cursor-pointer'
                                                    />
                                                </div>
                                            ) : vendorChangeRequest.delete_address ? (
                                                <div className='flex justify-between bg-[#FDEAEB] p-2 font-bold'>
                                                    <span className='line-through'>
                                                        {vendorChangeRequest.zip_code}
                                                    </span>
                                                    <RxCross2
                                                        color='#F02D3A'
                                                        size={22}
                                                        className='cursor-pointer'
                                                    />
                                                </div>
                                            ) : (
                                                <div>
                                                    {vendorChangeRequest.zip_code}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </>
                            )}

                            {/* Direct Deposit Information */}
                            {(vendorChangeRequest.ach_document_filename || hasNewBankingInfo()) && (
                                <>
                                    <h3 className='mb-3 text-[18px]/[22px] font-medium uppercase tracking-[1.44px] text-[#131212]'>
                                        Direct Deposit Information
                                    </h3>

                                    {/* Payment Method */}
                                    <div className='form-group mb-4'>
                                        <label className='mb-2 block font-bold'>Payment Method</label>
                                        <div className='flex gap-2'>
                                            <span
                                                className={`rounded-full px-5 py-1.5 text-sm font-bold ${
                                                    vendorChangeRequest.preferred_payment_method === 'CHECK'
                                                        ? 'bg-[#00A8A8] text-white'
                                                        : 'border border-black bg-white text-black'
                                                }`}
                                            >
                                                Pay by Check
                                            </span>
                                            <span
                                                className={`rounded-full px-5 py-1.5 text-sm font-bold ${
                                                    vendorChangeRequest.preferred_payment_method === 'ACH'
                                                        ? 'bg-[#00A8A8] text-white'
                                                        : 'border border-black bg-white text-black'
                                                }`}
                                            >
                                                Pay by Direct Deposit (ACH)
                                            </span>
                                        </div>
                                    </div>

                                    {/* Payee Email and Account Type */}
                                    <div className='form-group mb-3 flex gap-8'>
                                        <div className='flex-1'>
                                            <label className='mb-2 block font-bold'>
                                                Payee Email <span className='text-red-500'>*</span>
                                            </label>
                                            <div>{vendorChangeRequest.email || ''}</div>
                                        </div>
                                        <div className='flex-1'>
                                            <label className='mb-2 block font-bold'>
                                                Account Type <span className='text-red-500'>*</span>
                                            </label>
                                            <div>{formatAccountType(vendorChangeRequest.account_type)}</div>
                                        </div>
                                    </div>

                                    {/* Routing Number and Account Number */}
                                    <div className='form-group mb-4 flex gap-8'>
                                        <div className='flex-1'>
                                            <label className='mb-2 block font-bold'>
                                                Routing Number <span className='text-red-500'>*</span>
                                            </label>
                                            <div className='flex items-center gap-2'>
                                                <span>{vendorChangeRequest.routing_number || ''}</span>
                                                {vendorChangeRequest.routing_number && (
                                                    <button
                                                        type='button'
                                                        onClick={() => handleCopy(vendorChangeRequest.routing_number!, 'Routing number')}
                                                        className='rounded p-1 transition-colors hover:bg-[#E6F7F8]'
                                                        style={{ color: '#00AEB3' }}
                                                        title='Copy routing number'
                                                    >
                                                        <MdContentCopy size={18} />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        <div className='flex-1'>
                                            <label className='mb-2 block font-bold'>
                                                Account Number <span className='text-red-500'>*</span>
                                            </label>
                                            <div className='flex items-center gap-2'>
                                                <span>{vendorChangeRequest.account_number || ''}</span>
                                                {vendorChangeRequest.account_number && (
                                                    <button
                                                        type='button'
                                                        onClick={() => handleCopy(vendorChangeRequest.account_number!, 'Account number')}
                                                        className='rounded p-1 transition-colors hover:bg-[#E6F7F8]'
                                                        style={{ color: '#00AEB3' }}
                                                        title='Copy account number'
                                                    >
                                                        <MdContentCopy size={18} />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* ACH Image */}
                                    {vendorChangeRequest.ach_document_url && (
                                        <div className='form-group mb-3'>
                                            <label className='mb-2 block font-bold'>ACH Image</label>
                                            {(() => {
                                                const filename = vendorChangeRequest.ach_document_filename || '';
                                                const url = vendorChangeRequest.ach_document_url;
                                                const imageExtensions = /\.(jpg|jpeg|png|gif|webp|bmp)$/i;
                                                const isImage = imageExtensions.test(filename) || imageExtensions.test(url);

                                                return isImage ? (
                                                    <div
                                                        className='cursor-pointer border-2 border-dashed border-[#AFC12D] p-1'
                                                        onClick={() => setOpenLightbox(true)}
                                                    >
                                                        <img
                                                            src={url}
                                                            alt='ACH Document'
                                                            className='max-h-48 w-full object-contain'
                                                        />
                                                    </div>
                                                ) : (
                                                    <a
                                                        href={url}
                                                        target='_blank'
                                                        rel='noopener noreferrer'
                                                        className='text-blue-500 hover:underline'
                                                    >
                                                        {filename || 'View Document'}
                                                    </a>
                                                );
                                            })()}
                                        </div>
                                    )}
                                </>
                            )}
                        </Panel>
                    </div>
                </section>
            </div>

            {vendorChangeRequest?.ach_document_url && (
                <FundrequestLightbox
                    isOpen={openLightbox}
                    onClose={() => setOpenLightbox(false)}
                    uploads={[
                        {
                            image_url: vendorChangeRequest.ach_document_url,
                            filename: vendorChangeRequest.ach_document_filename || 'ACH Document',
                            id: 1,
                            fund_request_item: 1,
                            created_date: new Date().toISOString(),
                            created_by: 1
                        }
                    ]}
                    slideIndex={0}
                />
            )}
        </>
    );
};

export default RequestChangeDetail;
